import React, { useState } from "react";
import { User } from "../../../types/UserTypes";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Key as KeyIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { useUser } from "../../../contexts/UserContext";

interface Props {
  coaches: User[];
  currentUser: User | null;
}

interface DialogState {
  invite: boolean;
  delete: boolean;
  password: boolean;
  edit: boolean;
}

export const Coaches = ({ coaches, currentUser }: Props) => {
  const { inviteUser, deleteUser, changePassword } = useUser();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>({
    invite: false,
    delete: false,
    password: false,
    edit: false,
  });

  // Form states
  const [inviteForm, setInviteForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message: string, severity: "success" | "error") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  const handleDialogOpen = (dialog: keyof DialogState, user?: User) => {
    if (user) {
      setSelectedUser(user);
    }
    setDialogState({ ...dialogState, [dialog]: true });
  };

  const handleDialogClose = (dialog: keyof DialogState) => {
    setDialogState({ ...dialogState, [dialog]: false });
    setSelectedUser(null);
    // Reset forms
    if (dialog === "invite") {
      setInviteForm({ first_name: "", last_name: "", email: "" });
    } else if (dialog === "password") {
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const handleInviteSubmit = async () => {
    try {
      if (!inviteForm.first_name || !inviteForm.last_name || !inviteForm.email) {
        showNotification("Please fill in first name, last name, and email", "error");
        return;
      }

      if (!currentUser?.club?.id) {
        showNotification("Current user does not have an assigned club", "error");
        return;
      }

      await inviteUser({
        first_name: inviteForm.first_name,
        last_name: inviteForm.last_name,
        email: inviteForm.email,
        club: currentUser.club.id,
        user_type: "COACH",
      });
      showNotification("Coach invited successfully", "success");
      handleDialogClose("invite");
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : "Failed to invite coach",
        "error"
      );
    }
  };

  const handleDeleteUser = async () => {
    try {
      if (!selectedUser) return;

      await deleteUser(selectedUser.id);
      showNotification("User deleted successfully", "success");
      handleDialogClose("delete");
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : "Failed to delete user",
        "error"
      );
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (!passwordForm.currentPassword || !passwordForm.newPassword) {
        showNotification("Please fill in all fields", "error");
        return;
      }

      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        showNotification("New passwords do not match", "error");
        return;
      }

      await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );
      showNotification("Password changed successfully", "success");
      handleDialogClose("password");
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : "Failed to change password",
        "error"
      );
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={2}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={() => handleDialogOpen("invite")}
        >
          Invite Coach
        </Button>
      </Box>

      <List>
        {coaches.map((coach) => (
          <React.Fragment key={coach.id}>
            <ListItem>
              <ListItemText primary={coach.username} secondary={coach.email} />
              <ListItemSecondaryAction>
                {currentUser?.id === coach.id ? (
                  <IconButton
                    edge="end"
                    aria-label="change password"
                    onClick={() => handleDialogOpen("password", coach)}
                  >
                    <KeyIcon />
                  </IconButton>
                ) : (
                  <>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleDialogOpen("edit", coach)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDialogOpen("delete", coach)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>

      {/* Invite Coach Dialog */}
      <Dialog
        open={dialogState.invite}
        onClose={() => handleDialogClose("invite")}
      >
        <DialogTitle>Invite New Coach</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="First Name"
            fullWidth
            value={inviteForm.first_name}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, first_name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            value={inviteForm.last_name}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, last_name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={inviteForm.email}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, email: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose("invite")}>Cancel</Button>
          <Button
            onClick={handleInviteSubmit}
            color="primary"
            variant="contained"
          >
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={dialogState.delete}
        onClose={() => handleDialogClose("delete")}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selectedUser?.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose("delete")}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={dialogState.password}
        onClose={() => handleDialogClose("password")}
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Current Password"
            type="password"
            fullWidth
            value={passwordForm.currentPassword}
            onChange={(e) =>
              setPasswordForm({
                ...passwordForm,
                currentPassword: e.target.value,
              })
            }
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={passwordForm.newPassword}
            onChange={(e) =>
              setPasswordForm({ ...passwordForm, newPassword: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type="password"
            fullWidth
            value={passwordForm.confirmPassword}
            onChange={(e) =>
              setPasswordForm({
                ...passwordForm,
                confirmPassword: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose("password")}>Cancel</Button>
          <Button
            onClick={handlePasswordChange}
            color="primary"
            variant="contained"
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
